import { type QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import i18next from 'i18next'
import { useCallback } from 'react'

import { useAuth } from '@/features/auth/hooks/useAuth'
import type { ChatStreamState } from '@/features/chat-stream/types'
import { trackEvent } from '@/shared/lib/analytics'
import { notify } from '@/shared/lib/notify'
import { runUndoable } from '@/shared/lib/runUndoable'

import { playIntentsApi } from '../api/playIntentsApi'
import { playIntentErrorKey } from '../lib/playIntentErrors'
import type {
  CreatePlayIntentRequest,
  PlayIntent,
  PlayIntentGroup,
} from '../types/playIntent.types'

/**
 * Query keys. Todo cuelga de `['play-intents']` para que crear/retirar
 * invalide de una vez los grupos (de cualquier ciudad) y "Mis ganas".
 */
export const playIntentKeys = {
  all: ['play-intents'] as const,
  windows: () => [...playIntentKeys.all, 'windows'] as const,
  // El viewer cambia la respuesta (`mine`, avatares sin bloqueados): va en la key.
  allGroups: () => [...playIntentKeys.all, 'groups'] as const,
  groups: (cityCode: string, viewerId: number | null) =>
    [...playIntentKeys.allGroups(), cityCode, viewerId ?? 'anon'] as const,
  mine: () => [...playIntentKeys.all, 'mine'] as const,
  /** Bandeja de chats de grupo (`/me/play-intent-chats`). */
  chats: () => [...playIntentKeys.all, 'chats'] as const,
}

/** Franjas elegibles ahora. Públicas; cambian como mucho una vez al día. */
export function usePlayIntentWindowsQuery(enabled = true) {
  return useQuery({
    queryKey: playIntentKeys.windows(),
    queryFn: playIntentsApi.windows,
    enabled,
    staleTime: 10 * 60_000,
  })
}

/** Grupos activos de una ciudad. Público (con token rellena `mine`). */
export function usePlayIntentGroupsQuery(cityCode: string | undefined, enabled = true) {
  const { user } = useAuth()
  return useQuery({
    queryKey: playIntentKeys.groups(cityCode ?? '', user?.userId ?? null),
    queryFn: () => playIntentsApi.groups(cityCode!),
    enabled: enabled && Boolean(cityCode),
    staleTime: 60_000,
  })
}

/**
 * Mis chats de grupo para la bandeja `/messages`. Sondea como `/me/chats` solo si el
 * tiempo real no está (los mensajes nuevos llegan por SSE e invalidan esta lista).
 */
export function usePlayIntentChatsQuery(streamState?: ChatStreamState) {
  const { isAuthenticated } = useAuth()
  const shouldPoll = streamState === undefined || streamState === 'POLLING_FALLBACK'
  return useQuery({
    queryKey: playIntentKeys.chats(),
    queryFn: playIntentsApi.myChats,
    enabled: isAuthenticated,
    refetchInterval: shouldPoll ? 30_000 : false,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  })
}

/** Mis ganas activas. `/me/*` → solo con sesión (si no, 401 → rebote a /login). */
export function useMyPlayIntentsQuery() {
  const { isAuthenticated } = useAuth()
  return useQuery({
    queryKey: playIntentKeys.mine(),
    queryFn: playIntentsApi.mine,
    enabled: isAuthenticated,
  })
}

/**
 * Crear ganas. `source` distingue en la analítica (Umami) declarar unas ganas nuevas
 * (`Play Intent Create`) de sumarse a un grupo con "Yo también" (`Play Intent Join`):
 * para el backend son la misma llamada.
 */
export function useCreatePlayIntentMutation(source: 'create' | 'join') {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: CreatePlayIntentRequest) => playIntentsApi.create(body),
    onSuccess: () => {
      trackEvent(source === 'join' ? 'Play Intent Join' : 'Play Intent Create')
      notify.success(
        i18next.t(source === 'join' ? 'playIntents.toasts.joined' : 'playIntents.toasts.created'),
      )
      void queryClient.invalidateQueries({ queryKey: playIntentKeys.all })
    },
    // "Yo también" no tiene formulario donde pintar el error → toast. El modal de
    // crear lo pinta inline en su propio banner.
    onError: (err) => {
      if (source === 'join') notify.error(i18next.t(playIntentErrorKey(err)))
    },
  })
}

/**
 * Retirar unas ganas (→ WITHDRAWN): la petición real. El aviso de éxito lo da
 * {@link useWithdrawPlayIntentWithUndo} (con «Deshacer»); aquí solo el error. Se
 * invalida siempre al acabar: si falla, la caché optimista vuelve a lo real.
 */
export function useWithdrawPlayIntentMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => playIntentsApi.withdraw(id),
    onError: (err) => {
      notify.error(i18next.t(playIntentErrorKey(err)))
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: playIntentKeys.all })
    },
  })
}

/**
 * «Salir del chat» del grupo (ya con mesa). Al acabar refresca la bandeja y los grupos
 * (el «Chat» del grupo desaparece); el error se pinta como un toast.
 */
export function useLeaveGroupChatMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (intentId: number) => playIntentsApi.leaveChat(intentId),
    onError: (err) => {
      notify.error(i18next.t(playIntentErrorKey(err)))
    },
    onSuccess: () => {
      notify.success(i18next.t('playIntents.chat.leftToast'))
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: playIntentKeys.chats() })
      void queryClient.invalidateQueries({ queryKey: playIntentKeys.allGroups() })
    },
  })
}

/**
 * «Retirar» con «Deshacer» (`runUndoable`): las ganas desaparecen al momento de
 * "Mis ganas" y del grupo (caché optimista), y el DELETE solo sale al acabar la
 * ventana. Deshacer restaura la caché sin llamar al servidor.
 */
export function useWithdrawPlayIntentWithUndo() {
  const queryClient = useQueryClient()
  const { mutate } = useWithdrawPlayIntentMutation()
  const { user } = useAuth()
  const myNick = user?.username

  return useCallback(
    (intentId: number) => {
      let snapshot: [QueryKey, unknown][] = []
      runUndoable({
        message: i18next.t('playIntents.toasts.withdrawn'),
        undoLabel: i18next.t('common.undo'),
        apply: () => {
          void queryClient.cancelQueries({ queryKey: playIntentKeys.all })
          snapshot = [
            ...queryClient.getQueriesData({ queryKey: playIntentKeys.mine() }),
            ...queryClient.getQueriesData({ queryKey: playIntentKeys.allGroups() }),
          ]
          queryClient.setQueryData<PlayIntent[]>(playIntentKeys.mine(), (old) =>
            old?.filter((i) => i.id !== intentId),
          )
          queryClient.setQueriesData<PlayIntentGroup[]>(
            { queryKey: playIntentKeys.allGroups() },
            (old) => old && withoutMyIntent(old, intentId, myNick),
          )
        },
        revert: () => {
          for (const [key, data] of snapshot) queryClient.setQueryData(key, data)
        },
        commit: () => mutate(intentId),
      })
    },
    [queryClient, mutate, myNick],
  )
}

/** Grupos tal y como quedan sin mis ganas `intentId` (el grupo vacío desaparece). */
function withoutMyIntent(
  groups: PlayIntentGroup[],
  intentId: number,
  myNick: string | undefined,
): PlayIntentGroup[] {
  return groups.flatMap((g) => {
    if (g.myIntentId !== intentId) return [g]
    if (g.count <= 1) return []
    return [
      {
        ...g,
        count: g.count - 1,
        members: g.members.filter((m) => m.username !== myNick),
        mine: false,
        myIntentId: null,
      },
    ]
  })
}
